import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { uiMessages } from "@/i18n/messages/ui";
import { cn } from "@/lib/cn";
import { Button } from "./button";
import { confirmDialogPanelVariants } from "./confirm-dialog";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "./surface-contract";
import { TextInput } from "./text-input";

interface InputDialogProps {
  title: string;
  inputLabel: string;
  initialValue: string;
  confirmLabel?: string;
  errorMessage?: string;
  maxLength?: number;
  onCancel: () => void;
  onSubmit: (value: string) => Promise<void>;
  "data-testid"?: string;
}

/** Shared single-value dialog for rename and label-edit operations. */
export function InputDialog({
  title,
  inputLabel,
  initialValue,
  confirmLabel,
  errorMessage,
  maxLength,
  onCancel,
  onSubmit,
  "data-testid": testId = "input-dialog",
}: InputDialogProps) {
  const t = useT(uiMessages);
  const tc = useT(commonMessages);
  const [draft, setDraft] = useState(initialValue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const trimmed = draft.trim();

  async function submit() {
    if (!trimmed || trimmed === initialValue || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit(trimmed);
    } catch {
      setError(errorMessage ?? t("saveChangeFailed"));
      setSaving(false);
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[31800] flex items-center justify-center bg-black/30"
      onClick={onCancel}
      data-interactive="true"
      data-testid={`${testId}-backdrop`}
    >
      <form
        className={cn(
          surfaceRoleClassName("menu"),
          confirmDialogPanelVariants(),
        )}
        onClick={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        data-testid={testId}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${testId}-title`}
        {...surfaceRoleAttributes("menu", {
          nativeLevel: "root",
          nativeGroup: "menus",
        })}
      >
        <div className="border-b border-glass-stroke-faint px-5 py-3">
          <h2
            id={`${testId}-title`}
            className="text-appkit-body font-semibold text-primary"
          >
            {title}
          </h2>
        </div>
        <div className="px-5 py-4">
          <TextInput
            ref={inputRef}
            value={draft}
            maxLength={maxLength}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== "Escape") return;
              event.preventDefault();
              event.stopPropagation();
              onCancel();
            }}
            aria-label={inputLabel}
            data-testid={`${testId}-input`}
          />
          {error ? (
            <p className="mt-2 text-appkit-caption text-danger" role="alert">
              {error}
            </p>
          ) : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-glass-stroke-faint px-5 py-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={saving}
            data-testid={`${testId}-cancel`}
          >
            {tc("cancel")}
          </Button>
          <Button
            type="submit"
            disabled={!trimmed || trimmed === initialValue || saving}
            data-testid={`${testId}-confirm`}
          >
            {saving ? t("saving") : (confirmLabel ?? tc("save"))}
          </Button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
